import axios from 'axios'
import {API_URL, fixNumbers} from '../../../../_metronic/helpers'
import {toPersianDigits} from '../../../../_metronic/helpers/components/datepicker/persianDateValue'
import type {AuthModel} from './_models'
import {isStaff} from './staffAccess'

// «تکمیل حساب کاربری» (user decision 2026-10-29): every person signs in as themselves — a confirmed mobile, a first
// and last name, and a username and password of their own (the password is the way in on days the SMS gateway is
// down). Until the account has them the server answers every other request with 403 ACCOUNT_INCOMPLETE and the
// panel shows only the completion page. The server decides; the panel only asks for what it says is missing.

export {ACCOUNT_INCOMPLETE, ACCOUNT_INCOMPLETE_EVENT} from './accountGateCodes'

/** What is still missing, as the session's user carries it (TokenReturn.accountCompletion). */
export type AccountCompletionFlags = {
  name: boolean
  mobile: boolean
  credentials: boolean
  /** The server saw the SMS fail for this user a moment ago: «فعلاً ادامه» for 8 hours is allowed. */
  canDeferMobile: boolean
}

export type CompletionMoment = {day: string; time: string}

/** GET api/account/completion: the details of each step. */
export type AccountCompletionState = {
  name: {needed: boolean; firstName?: string | null; lastName?: string | null}
  mobile: {
    needed: boolean
    deferred: boolean
    canDefer: boolean
    deferHours: number
    /**
     * This session rests on a username and password alone, on an account that already has a number (backend 1.0.0.769+):
     * that number, masked. It never changes; the person proves their own, and another number means an account of their own.
     */
    accountMobile?: string | null
  }
  credentials: {needed: boolean; username?: string | null; reason?: 'missing' | 'mobile-added'}
}

export type MobileCodeSent = {
  destination: string
  expiresInSeconds: number
  resendInSeconds: number
}

/** What proving a number did to a password sign-in on an account that has one. */
export type OwnMobileOutcome = 'same-account' | 'new-account' | 'existing-account'

export type MobileConfirmed = {
  user: AuthModel
  merged: boolean
  mergedBusinesses: number
  ownMobile?: OwnMobileOutcome
  copiedBusinesses?: number
}

export type CompletionProfileInput = {
  firstName?: string
  lastName?: string
  /** Wrapped with wrapCredential, like every username and password the panel sends. */
  username?: string
  password?: string
}

// Built when called, never while the module loads (the helpers may not be initialised yet).
const completionUrl = () => `${API_URL}/account/completion`

/** A support or staff session is no customer account: it never sees the completion page (the server sends no flags). */
export const needsAccountCompletion = (
  user?: Pick<AuthModel, 'accountCompletion' | 'support' | 'staff'> | null
) => Boolean(user?.accountCompletion) && !user?.support && !isStaff(user)

export const getAccountCompletion = () =>
  axios.get<AccountCompletionState>(completionUrl()).then((response) => response.data)

export const sendCompletionMobileCode = (mobile: string) =>
  axios
    .post<MobileCodeSent>(`${completionUrl()}/mobile/code`, {mobile})
    .then((response) => response.data)

export const ACCEPT_PERSONAL_USE_FIRST = 'برای ادامه، ابتدا تعهد بالا را بخوانید و بپذیرید.'

// Every request that changes the account carries the acknowledgment of «حساب کاربری شخصی و مسئولیت استفاده از آن»;
// the server refuses it without (400 PERSONAL_USE_REQUIRED). The page sends none of them before the box is ticked.

// acceptOwnMobile: the page has said that proving a number ends the password signed in with. The server refuses that step
// without it (409 PAGE_OUTDATED), so a tab still running an older panel never ends a password unseen.
export const confirmCompletionMobile = (mobile: string, code: string, acceptOwnMobile = false) =>
  axios
    .post<MobileConfirmed>(`${completionUrl()}/mobile`, {
      mobile,
      code,
      acceptPersonalUse: true,
      ...(acceptOwnMobile ? {acceptOwnMobile: true} : {}),
    })
    .then((response) => response.data)

export const deferCompletionMobile = () =>
  axios
    .post<{user: AuthModel}>(`${completionUrl()}/defer`, {acceptPersonalUse: true})
    .then((response) => response.data)

export const saveCompletionProfile = (input: CompletionProfileInput) =>
  axios
    .post<{user: AuthModel}>(`${completionUrl()}/profile`, {...input, acceptPersonalUse: true})
    .then((response) => response.data)

export type CompletionError = {
  status?: number
  code?: string
  message: string
  /** 503 SMS_FAILED says whether «فعلاً ادامه» is allowed now. */
  canDefer?: boolean
  retryAfterSeconds?: number
}

/** The server's own words when it gave them; a lost connection is said as such. */
export const readCompletionError = (error: unknown, fallback: string): CompletionError => {
  if (!axios.isAxiosError(error)) return {message: fallback}
  if (!error.response) {
    return {message: 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'}
  }
  const data = (error.response.data || {}) as {code?: string; message?: string; canDefer?: boolean}
  const retryAfter = Number(error.response.headers?.['retry-after'])
  return {
    status: error.response.status,
    code: data.code,
    message: typeof data.message === 'string' && data.message ? data.message : fallback,
    canDefer: Boolean(data.canDefer),
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  }
}

/** What the page tells the person after they proved a number, by what the server did with it. */
export const ownMobileOutcomeMessage = (result: MobileConfirmed) => {
  const access = result.copiedBusinesses
    ? ` دسترسی ${toPersianDigits(result.copiedBusinesses)} کسب‌وکار به آن داده شد و`
    : ''
  switch (result.ownMobile) {
    case 'same-account':
      return 'شماره شما تأیید شد. رمز قبلی این حساب برای امنیت باطل شد؛ نام کاربری و رمز شخصی تازه بگذارید.'
    case 'new-account':
      return `حساب شخصی شما با همین شماره ساخته شد؛${access} ورود با نام کاربری و رمز قبلی دیگر ممکن نیست.`
    case 'existing-account':
      return `این شماره از قبل حساب داشت و با همان حساب ادامه می‌دهید؛${access} ورود با نام کاربری و رمز قبلی دیگر ممکن نیست.`
    default:
      return ''
  }
}

/** The steps the page walks through, in order, for what the server says is missing. */
export type CompletionStep = 'mobile' | 'profile'

export const completionSteps = (
  state: AccountCompletionState | undefined,
  mode: 'all' | 'mobile' = 'all'
): CompletionStep[] => {
  if (!state) return []
  const steps: CompletionStep[] = []
  // While the hours given during an SMS outage run, the mobile is still needed but waits — except when the user opens
  // «ثبت موبایل» to give it now.
  const mobileDue = state.mobile.needed || state.mobile.deferred
  if (mode === 'mobile' ? mobileDue : state.mobile.needed && !state.mobile.deferred)
    steps.push('mobile')
  if (mode === 'all' && (state.name.needed || state.credentials.needed)) steps.push('profile')
  return steps
}

/** A mobile as typed (Persian digits, spaces) in the shape the server takes: 11 digits. */
export const normalizeMobileInput = (value: string) =>
  fixNumbers(value).replace(/\D/g, '').slice(0, 11)

export const isValidMobile = (value: string) => /^09\d{9}$/.test(normalizeMobileInput(value))
